import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { selectCohortDefinitionIsVersionLoading } from '@verantos-dev/app-state-cohort-definition';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { formatDate } from '@verantos-dev/shared-utils';
import {
  CohortBuilderModalLegacy,
  CohortStatusLegacy,
  IndexDateTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { deleteIndexDate, setModal } from '../cohortBuilderSlice';
import { useCohortEditable } from './useCohortEditable';
import {
  selectCohortBuilderItemIdsToNamesMap,
  selectInUseCohortBuilderItemsMap,
  selectModal,
} from '../selectors/cohortBuilderWorkflowSelectors';
import {
  selectCohortDsl,
  selectIndexDate,
  selectIndexDateId,
  selectIndexDateRange,
  selectIndexDateType,
  selectIndexDateValue,
} from '../selectors/cohortDefinitionSelectors';
import { getFullCohortDslForIndexDate } from '../utils/dsl-generators/getFullCohortDslForIndexDate';
import { getInUseCohortBuilderNamesForId } from '../utils/getInUseCohortBuilderNamesForId';

export const useIndexDateView = (minimalMode?: boolean) => {
  const dispatch = useAppDispatch();
  const id = uuidv4();
  const { data: cohortData } = useCohortBuilderData();
  const isProcessing =
    !!cohortData?.data.latestStatus &&
    [CohortStatusLegacy.PROCESSING, CohortStatusLegacy.FINAL].includes(
      cohortData?.data.latestStatus
    );
  const { isEditable } = useCohortEditable();
  const viewMode = !isEditable || isProcessing;
  const isLoading = useAppSelector(selectCohortDefinitionIsVersionLoading);

  const intl = useIntl();
  const currentCohortDsl = useAppSelector(selectCohortDsl);

  // select the index date and modal from the store
  const modal = useAppSelector(selectModal);
  const isIndexDateDefined = !!useAppSelector(selectIndexDate);
  const indexDateId = useAppSelector(selectIndexDateId) ?? id;
  const indexDateType = useAppSelector(selectIndexDateType) ?? '';
  const _indexDateValue = useAppSelector(selectIndexDateValue) ?? '';
  const inUseCohortBuilderItemsMap = useAppSelector(selectInUseCohortBuilderItemsMap);
  const cohortBuilderItemIdsToNamesMap = useAppSelector(selectCohortBuilderItemIdsToNamesMap);
  const indexDateRangeValue = useAppSelector(selectIndexDateRange);

  const showIndexDate =
    modal !== undefined &&
    [CohortBuilderModalLegacy.ADD_INDEX_DATE, CohortBuilderModalLegacy.EDIT_INDEX_DATE].includes(
      modal
    );

  const isSetIndexDateButtonDisabled = showIndexDate || isIndexDateDefined || modal !== undefined;
  const showSetIndexDateButton = isEditable && !(showIndexDate || isIndexDateDefined);

  const inUseItems = getInUseCohortBuilderNamesForId(
    indexDateId,
    inUseCohortBuilderItemsMap,
    cohortBuilderItemIdsToNamesMap
  );
  const isInUse = !!inUseItems && inUseItems.length > 0;

  /** Index date value calculation */
  let indexDateValue = _indexDateValue;
  if (indexDateType === IndexDateTypeLegacy.CALENDAR) {
    indexDateValue = formatDate(_indexDateValue, 'dateNumeric').toString();
  }
  if (indexDateType === IndexDateTypeLegacy.EVENT) {
    if (_indexDateValue) {
      const eventName = _indexDateValue.split('::');
      if (eventName.length === 2) {
        // enters here if the event name is in the format of INSTANCE_EVENT::[eventName]
        const trimmedEventName = eventName[1].trim();
        indexDateValue = trimmedEventName.slice(1, trimmedEventName.length - 1);
      }
      if (eventName.length === 1) {
        // enters here if the event name is in the format of [eventName]
        if (eventName[0] === 'birth_datetime') {
          indexDateValue = 'Birth datetime';
        }
      }
    }
  }

  /** Click Handlers */

  // open the modal to add an index date
  const onClickSetIndexDate = () => {
    !viewMode &&
      dispatch(
        setModal({
          id: indexDateId,
          modal: CohortBuilderModalLegacy.ADD_INDEX_DATE,
        })
      );
  };

  // open the modal to edit an index date
  const onClickEditIndexDate = () => {
    !viewMode &&
      dispatch(
        setModal({
          id: indexDateId,
          modal: CohortBuilderModalLegacy.EDIT_INDEX_DATE,
        })
      );
  };

  // delete the index date from the definition
  const onClickDeleteIndexDate = (emitResearchAssistantUpdate: (nextCohortDsl: string) => void) => {
    if (!isInUse) {
      dispatch(deleteIndexDate());
      const _nextCohortDsl = getFullCohortDslForIndexDate(undefined, currentCohortDsl);
      emitResearchAssistantUpdate(_nextCohortDsl);
    }
  };

  /** Fields */
  const fields = useMemo(() => {
    const _fields = [];

    // For empty state in minimal mode (research assistant)
    if (minimalMode && !isIndexDateDefined) {
      return [
        {
          label: intl.formatMessage({
            id: 'projects.cohortBuilderPage.indexDateViewCard.fieldTypeLabel',
          }),
          value: '--',
          flex: 0.5,
        },
        {
          label: intl.formatMessage({
            id: 'projects.cohortBuilderPage.indexDateViewCard.fieldDateLabel',
          }),
          value: '--',
        },
      ];
    }

    if (indexDateType) {
      _fields.push({
        label: intl.formatMessage({
          id: 'projects.cohortBuilderPage.indexDateViewCard.fieldTypeLabel',
        }),
        value: indexDateType,
        flex: 0.5,
      });

      if (indexDateType === IndexDateTypeLegacy.CALENDAR) {
        _fields.push({
          label: intl.formatMessage({
            id: 'projects.cohortBuilderPage.indexDateViewCard.fieldDateLabel',
          }),
          value: indexDateValue,
        });
      } else if (indexDateType === IndexDateTypeLegacy.EVENT) {
        _fields.push({
          label: intl.formatMessage({
            id: 'projects.cohortBuilderPage.indexDateViewCard.fieldEventLabel',
          }),
          value: indexDateValue,
        });
      }
    }

    if (indexDateRangeValue && indexDateRangeValue?.startDate && indexDateRangeValue?.endDate) {
      _fields.push({
        label: intl.formatMessage({
          id: 'projects.cohortBuilderPage.indexDateViewCard.fieldDateRangeLabel',
        }),
        value: `${dayjs.utc(indexDateRangeValue.startDate).format('MM/DD/YYYY')}\u2013${dayjs
          .utc(indexDateRangeValue.endDate)
          .format('MM/DD/YYYY')}`,
      });
    }

    return _fields;
  }, [indexDateRangeValue, indexDateType, indexDateValue, intl, isIndexDateDefined, minimalMode]);

  return {
    viewMode,
    isIndexDateDefined,
    showIndexDate,
    isInUse,
    inUseItems,
    isLoading,

    fields,
    isSetIndexDateButtonDisabled,
    showSetIndexDateButton,

    onClickSetIndexDate,
    onClickEditIndexDate,
    onClickDeleteIndexDate,
  };
};
